import {
  Box,
  Chip,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { ChevronRight } from "@mui/icons-material";
import type { PdfJobListItem } from "features/pdfTools/pdfTools";
import JobStatusChip from "./JobStatusChip";
import { PDF_JOB_RESULT_LABELS } from "./pdfJobUi";

interface JobsTableProps {
  rows: PdfJobListItem[];
  loading: boolean;
  onRowClick: (jobId: string) => void;
  pagination?: React.ReactNode;
}

const HEADER_CELLS = ["Client User", "Splitter", "Status", "Result", ""];

export default function JobsTable({
  rows,
  loading,
  onRowClick,
  pagination,
}: JobsTableProps) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        flex: 1,
      }}
    >
      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        <Table stickyHeader size="small" sx={{ minWidth: 680 }}>
          <TableHead>
            <TableRow>
              {HEADER_CELLS.map((label, idx) => (
                <TableCell
                  key={label || `col-${idx}`}
                  align="center"
                  sx={{ fontWeight: 700 }}
                >
                  {label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && rows.length === 0
              ? Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={`skeleton-${i}`}>
                    {HEADER_CELLS.map((_label, j) => (
                      <TableCell key={j} align="center">
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : rows.map((job) => (
                  <TableRow
                    key={job.job_id}
                    hover
                    onClick={() => onRowClick(job.job_id)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell align="center">
                      <Typography
                        noWrap
                        sx={{ fontSize: 14, maxWidth: 260, mx: "auto" }}
                      >
                        {job.user.username || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">{job.splitter_label}</TableCell>
                    <TableCell align="center">
                      <JobStatusChip status={job.status} />
                    </TableCell>
                    <TableCell align="center">
                      {job.result_state ? (
                        <Chip
                          size="small"
                          variant="outlined"
                          color={
                            job.result_state === "saved"
                              ? "success"
                              : job.result_state === "discarded"
                                ? "default"
                                : "info"
                          }
                          label={PDF_JOB_RESULT_LABELS[job.result_state]}
                        />
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell align="center" sx={{ width: 40 }}>
                      <ChevronRight
                        fontSize="small"
                        sx={{ color: "text.secondary" }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={HEADER_CELLS.length}>
                  <Box sx={{ py: 6, textAlign: "center" }}>
                    <Typography sx={{ fontWeight: 600 }}>
                      No split jobs yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Start one to split a multi-employee PDF into per-employee
                      files.
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
      {pagination}
    </Box>
  );
}
